import {
  ApprovalForm,
  type ApprovalFormFields,
} from "@/components/ApprovalForm/ApprovalForm";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import { useTranslation } from "react-i18next";
import { useModal } from "@/hooks/modals/useModal";
import { useFlow } from "@/hooks/services/useFlow";
import {
  SrmSupplierMaterialBlockSchema,
  SupplierBlockItemDetail,
  SupplierUserSimpleListItem,
} from "@/types/apis";
import type { FormInstance } from "antd";
import type { BlockApplyFormFields } from "../type";
import { FREEZE_APPLY_TYPE } from "@/constants";
import { notifySuccess } from "@/utils/notification";
import { useBlockRecordDetailQuery } from "./useBlockRecordDetailQuery";

export function useFormFooter(
  form: FormInstance<BlockApplyFormFields>,
  cleanup: () => void,
) {
  const { t } = useTranslation("supplierProfile");
  const { t: tCommon } = useTranslation("common");
  const { props, open } = useModal("MATERIAL_BLOCK");
  const {
    mode,
    taskId,
    recordId,
    onSuccess,
    supplier,
    flowStatus,
    viewStatus,
    isReadOnly,
  } = props;

  const handleSuccess = (message: string) => {
    notifySuccess(message);
    onSuccess?.();
    cleanup();
  };

  const detail = useBlockRecordDetailQuery(recordId);

  const createFlowCodeRules = [!detail, flowStatus !== t("审批中")];

  const shouldCreateFlowCode = createFlowCodeRules.some((rule) => rule);

  const { flowCode, startFlowMutation, passFlowMutation } = useFlow(
    "MATERIAL_BLOCK",
    open && shouldCreateFlowCode,
  );

  // 冻结申请
  const handleBlockApply = async () => {
    if (!flowCode || !supplier) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "MATERIAL_BLOCK",
        formNo: flowCode,
        variable: {
          supplierUserId: supplier.id,
          supplierUserName: supplier.supplierName,
        },
        srmSupplierMaterialBlockDTO: buildBlockDto(supplier, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("冻结申请已提交")),
      },
    );
  };

  // 重新提交冻结申请
  const handleBlockReapply = async () => {
    if (!flowCode || !detail) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "MATERIAL_BLOCK",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierMaterialBlockDTO: buildBlockDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("冻结申请已提交")),
      },
    );
  };

  // 解冻申请
  const handleUnblockApply = async () => {
    if (!flowCode || !detail) return;
    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "MATERIAL_BLOCK",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierMaterialBlockDTO: buildUnblockDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("解冻申请已提交")),
      },
    );
  };

  // 重新发起解冻申请
  const handleUnblockReapply = async () => {
    if (!flowCode || !detail) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "MATERIAL_BLOCK",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierMaterialBlockDTO: buildUnblockDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("解冻申请已提交")),
      },
    );
  };

  // 审批
  const handleApplyCheck = (values: ApprovalFormFields) => {
    if (!taskId) return;

    passFlowMutation.mutate(
      {
        taskId: taskId,
        flowParamsDto: {
          skipType: values.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: values.message,
          flowCode: "MATERIAL_BLOCK",
          variable: {},
        },
      },
      {
        onSuccess: () => handleSuccess(t("审批成功")),
      },
    );
  };

  const renderFooter = (detail?: SupplierBlockItemDetail) => {
    const Footer = {
      blockApply: (
        <PrimaryButton onClick={handleBlockApply}>
          {tCommon("提交")}
        </PrimaryButton>
      ),
      blockReapply: (
        <PrimaryButton onClick={handleBlockReapply}>
          {tCommon("提交")}
        </PrimaryButton>
      ),
      unblockApply: (
        <PrimaryButton onClick={handleUnblockApply}>
          {tCommon("提交")}
        </PrimaryButton>
      ),
      unblockReapply: (
        <PrimaryButton onClick={handleUnblockReapply}>
          {tCommon("提交")}
        </PrimaryButton>
      ),
      close: (
        <PrimaryOutlineButton onClick={cleanup}>
          {tCommon("关闭")}
        </PrimaryOutlineButton>
      ),
      approve: <ApprovalForm onApprovalSubmit={handleApplyCheck} />,
    };

    if (isReadOnly) return Footer.close;

    // 用户收到申请并审批(待办任务)
    if (mode === "inReview") {
      return viewStatus ? Footer.approve : Footer.close;
    }

    // 提交冻结申请
    if (!detail) return Footer.blockApply;

    // 用户发起申请
    const rules = [
      {
        // 重新提交冻结申请
        match:
          detail.applyType === FREEZE_APPLY_TYPE.PENDING_SUBMISSION &&
          flowStatus === "已驳回",
        render: Footer.blockReapply,
      },
      {
        // 提交解冻申请
        match:
          detail.applyType === FREEZE_APPLY_TYPE.PENDING_SUBMISSION &&
          flowStatus === "已通过",
        render: Footer.unblockApply,
      },
      {
        // 重新提交解冻申请
        match:
          detail.applyType === FREEZE_APPLY_TYPE.UNDER_APPROVAL &&
          flowStatus === "已驳回",
        render: Footer.unblockReapply,
      },
    ];

    const rule = rules.find((r) => r.match);
    return rule ? rule.render : Footer.close;
  };

  return renderFooter;
}

function buildBlockDto(
  base: SupplierUserSimpleListItem | SupplierBlockItemDetail,
  flowCode: string,
  values: BlockApplyFormFields,
): SrmSupplierMaterialBlockSchema {
  return {
    processCode: flowCode,
    supplierUserId: "supplierName" in base ? base.id : base.supplierUserId,
    supplierCode: base.supplierCode,
    supplierUserName:
      "supplierName" in base ? base.supplierName : base.supplierUserName,
    supplierUsUserName:
      "supplierName" in base ? base.supplierUsName : base.supplierUsUserName,
    blockType: values.blockType,
    blockedFactoryValues: values.blockedFactoryValues?.join(),
    blockedFactoryNames: values.blockedFactoryNames,
    blockedMaterialValues: values.blockedMaterialValues?.join(),
    blockedMaterialNames: values.blockedMaterialNames,
    fileIds: values.files?.map((item) => item.uid),
    blockReason: values.blockReason,
    applyType: FREEZE_APPLY_TYPE.PENDING_SUBMISSION,
  };
}

function buildUnblockDto(
  detail: SupplierBlockItemDetail,
  flowCode: string,
  values: BlockApplyFormFields,
): SrmSupplierMaterialBlockSchema {
  return {
    processCode: flowCode,
    supplierUserId: detail.supplierUserId,
    supplierCode: detail.supplierCode,
    supplierUserName: detail.supplierUserName,
    supplierUsUserName: detail.supplierUsUserName,
    blockType: values.blockType,
    blockedFactoryValues: detail.blockedFactoryValues,
    blockedFactoryNames: detail.blockedFactoryNames,
    blockedMaterialValues: detail.blockedMaterialValues,
    blockedMaterialNames: detail.blockedMaterialNames,
    fileIds: detail.srmSupplierFlowBusinessFiles.map((item) =>
      item.id.toString(),
    ),
    blockReason: detail.blockReason,
    unblockReason: values.unblockReason,
    applyType: FREEZE_APPLY_TYPE.UNDER_APPROVAL,
  };
}
